import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Rating,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import { judgingService } from '../services/judgingService';

const JudgingComments = ({ storyId, storyTitle }) => {
  const [judgingComments, setJudgingComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchJudgingComments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await judgingService.getJudgingCommentsForMyStory(storyId);
      console.log('Judging comments response:', response); // Debug log
      setJudgingComments(response.data?.judging_comments || response.judging_comments || []);
    } catch (err) {
      console.error('Error fetching judging comments:', err);
      if (err.response?.status === 403) {
        setError('You can only view judging comments on your own stories.');
      } else if (err.response?.status === 404) {
        setError('Story not found.');
      } else {
        setError('Failed to load judging comments.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewComments = () => {
    setDialogOpen(true);
    if (judgingComments.length === 0) {
      fetchJudgingComments();
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const getScoreColor = (score) => {
    if (score >= 4) return 'success';
    if (score >= 3) return 'warning';
    return 'error';
  };

  const averageScore = judgingComments.length > 0 
    ? judgingComments
        .filter(comment => comment.score)
        .reduce((sum, comment) => sum + comment.score, 0) / 
      judgingComments.filter(comment => comment.score).length
    : null;

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GavelIcon color="primary" />
              <Typography variant="h6">
                Judge Feedback
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={handleViewComments}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'View Feedback'}
            </Button>
          </Box>
          
          {judgingComments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip 
                  label={`${judgingComments.length} Judge${judgingComments.length !== 1 ? 's' : ''}`}
                  color="primary"
                  size="small"
                />
                {averageScore && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Average Score:</Typography>
                    <Rating value={averageScore} readOnly size="small" />
                    <Chip 
                      label={averageScore.toFixed(1)}
                      color={getScoreColor(averageScore)}
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Judge Feedback for "{storyTitle}"
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading judge feedback...
              </Typography>
            </Box>
          ) : judgingComments.length === 0 ? (
            <Alert severity="info">
              No judge feedback available for this story yet.
            </Alert>
          ) : (
            <List>
              {judgingComments.map((comment, index) => (
                <React.Fragment key={comment.comment_id}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" color="primary">
                          {comment.judge?.display_name || `Judge ${index + 1}`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {comment.score && (
                            <>
                              <Rating value={comment.score} readOnly size="small" />
                              <Chip 
                                label={comment.score}
                                color={getScoreColor(comment.score)}
                                size="small"
                              />
                            </>
                          )}
                          {comment.is_final && (
                            <Chip label="Final" color="success" size="small" />
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                        {comment.content}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(comment.created_at).toLocaleDateString()} at{' '}
                        {new Date(comment.created_at).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  </ListItem>
                  {index < judgingComments.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {judgingComments.length > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> Judge identities are kept anonymous to ensure fair and unbiased feedback. 
                Scores range from 1-5 stars, with 5 being the highest rating.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JudgingComments;
